import { c as _c } from "react/compiler-runtime";
import { fetchBjHome, fetchMobileMainInfo, fetchWebMainInfo, fetchWebMainPage } from '@module/apis/client-side/main';
import type { BjHomeResponse, MobileMainInfo, WebMainInfoResponse, WebMainLayoutDefaultParam, WebMainPageResponse, WebMainParam } from '@module/apis/client-side/main/type';
import { attachAdultShowAdModeParamIfEligible } from '@module/helpers/adult/adultShowAdMode';
import { useAdultShowAdModeEligibility } from '@module/helpers/adult/useAdultShowAdMode';
import usePartnerStore from '@module/stores/usePartnerStore';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import TimeConstants from '../../utils/item';
import type { CustomUseQueryOptions } from '../type';
import { mainQueryKeys } from './mainQueryKeys';
export const useFetchWebMainPageQuery = (params, options) => {
  const $ = _c(19);
  const {
    AdultShowAdMode
  } = usePartnerStore();
  const {
    isResolved,
    adultToggleShow
  } = useAdultShowAdModeEligibility();
  let finalParams;
  let resolvedEnabled;
  let t0;
  let t1;
  if ($[0] !== AdultShowAdMode || $[1] !== adultToggleShow || $[2] !== isResolved || $[3] !== options?.enabled || $[4] !== params) {
    finalParams = attachAdultShowAdModeParamIfEligible(params, {
      adultShowAdMode: AdultShowAdMode,
      adultToggleShow,
      isResolved
    });
    resolvedEnabled = (options?.enabled ?? true) && isResolved;
    t1 = useQuery;
    t0 = mainQueryKeys.fetchWebMainPage(finalParams);
    $[0] = AdultShowAdMode;
    $[1] = adultToggleShow;
    $[2] = isResolved;
    $[3] = options?.enabled;
    $[4] = params;
    $[5] = finalParams;
    $[6] = resolvedEnabled;
    $[7] = t0;
    $[8] = t1;
  } else {
    finalParams = $[5];
    resolvedEnabled = $[6];
    t0 = $[7];
    t1 = $[8];
  }
  let t2;
  if ($[9] !== finalParams) {
    t2 = () => fetchWebMainPage(finalParams);
    $[9] = finalParams;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== options || $[12] !== resolvedEnabled || $[13] !== t0.queryKey || $[14] !== t2) {
    t3 = {
      queryKey: t0.queryKey,
      queryFn: t2,
      staleTime: TimeConstants.HALF_SECOND * 10,
      ...options,
      enabled: resolvedEnabled,
      placeholderData: keepPreviousData
    };
    $[11] = options;
    $[12] = resolvedEnabled;
    $[13] = t0.queryKey;
    $[14] = t2;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  const query = t1(t3);
  let t4;
  if ($[16] !== finalParams || $[17] !== query) {
    t4 = {
      ...query,
      finalParams
    };
    $[16] = finalParams;
    $[17] = query;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  return t4;
};
export const useFetchWebMainInfoQuery = (params, options) => {
  const $ = _c(19);
  const {
    AdultShowAdMode
  } = usePartnerStore();
  const {
    isResolved,
    adultToggleShow
  } = useAdultShowAdModeEligibility();
  let finalParams;
  let resolvedEnabled;
  let t0;
  let t1;
  if ($[0] !== AdultShowAdMode || $[1] !== adultToggleShow || $[2] !== isResolved || $[3] !== options?.enabled || $[4] !== params) {
    finalParams = attachAdultShowAdModeParamIfEligible(params || {}, {
      adultShowAdMode: AdultShowAdMode,
      adultToggleShow,
      isResolved
    });
    resolvedEnabled = (options?.enabled ?? true) && isResolved;
    t1 = useQuery;
    t0 = mainQueryKeys.fetchWebMainInfo(finalParams);
    $[0] = AdultShowAdMode;
    $[1] = adultToggleShow;
    $[2] = isResolved;
    $[3] = options?.enabled;
    $[4] = params;
    $[5] = finalParams;
    $[6] = resolvedEnabled;
    $[7] = t0;
    $[8] = t1;
  } else {
    finalParams = $[5];
    resolvedEnabled = $[6];
    t0 = $[7];
    t1 = $[8];
  }
  let t2;
  if ($[9] !== finalParams) {
    t2 = () => fetchWebMainInfo(finalParams);
    $[9] = finalParams;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== options || $[12] !== resolvedEnabled || $[13] !== t0.queryKey || $[14] !== t2) {
    t3 = {
      queryKey: t0.queryKey,
      queryFn: t2,
      staleTime: TimeConstants.HALF_SECOND * 10,
      refetchOnMount: false,
      ...options,
      enabled: resolvedEnabled,
      placeholderData: keepPreviousData
    };
    $[11] = options;
    $[12] = resolvedEnabled;
    $[13] = t0.queryKey;
    $[14] = t2;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  const query = t1(t3);
  let t4;
  if ($[16] !== finalParams || $[17] !== query) {
    t4 = {
      ...query,
      finalParams
    };
    $[16] = finalParams;
    $[17] = query;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  return t4;
};
export const useFetchBjHome = options => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = mainQueryKeys.fetchBjHome();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== options) {
    t1 = {
      queryKey: t0.queryKey,
      queryFn: _temp,
      ...options
    };
    $[1] = options;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useQuery(t1);
};
export const useFetchMobileMainInfo = (params, options) => {
  const $ = _c(19);
  const {
    AdultShowAdMode
  } = usePartnerStore();
  const {
    isResolved,
    adultToggleShow
  } = useAdultShowAdModeEligibility();
  let finalParams;
  let resolvedEnabled;
  let t0;
  let t1;
  if ($[0] !== AdultShowAdMode || $[1] !== adultToggleShow || $[2] !== isResolved || $[3] !== options?.enabled || $[4] !== params) {
    finalParams = attachAdultShowAdModeParamIfEligible(params, {
      adultShowAdMode: AdultShowAdMode,
      adultToggleShow,
      isResolved
    });
    resolvedEnabled = (options?.enabled ?? true) && isResolved;
    t1 = useQuery;
    t0 = mainQueryKeys.fetchMobileMainInfo(finalParams);
    $[0] = AdultShowAdMode;
    $[1] = adultToggleShow;
    $[2] = isResolved;
    $[3] = options?.enabled;
    $[4] = params;
    $[5] = finalParams;
    $[6] = resolvedEnabled;
    $[7] = t0;
    $[8] = t1;
  } else {
    finalParams = $[5];
    resolvedEnabled = $[6];
    t0 = $[7];
    t1 = $[8];
  }
  let t2;
  if ($[9] !== finalParams) {
    t2 = () => fetchMobileMainInfo(finalParams);
    $[9] = finalParams;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== options || $[12] !== resolvedEnabled || $[13] !== t0.queryKey || $[14] !== t2) {
    t3 = {
      queryKey: t0.queryKey,
      queryFn: t2,
      staleTime: TimeConstants.HALF_SECOND * 10,
      ...options,
      enabled: resolvedEnabled
    };
    $[11] = options;
    $[12] = resolvedEnabled;
    $[13] = t0.queryKey;
    $[14] = t2;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  const query = t1(t3);
  let t4;
  if ($[16] !== finalParams || $[17] !== query) {
    t4 = {
      ...query,
      finalParams
    };
    $[16] = finalParams;
    $[17] = query;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  return t4;
};
function _temp() {
  return fetchBjHome();
}